import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import useLiterals from 'utils/hooks/useLiterals';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import { getContracts } from 'modules/contracts/actions';
import { getSections } from 'modules/sections/actions';
import { ROUTE_PATH } from 'routes';
import { renderIconCategories } from 'components/PaymentBox';
import documentIcon from 'assets/icons/document.svg';
import Box from 'components/UI/Box/Box';
import SimpleCardListPaginated from 'components/SimpleCardList/SimpleCardListPaginated';
import { translate } from 'utils/translate';
import { formatDate } from 'utils/formatDate';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import InlineError from 'components/UI/InlineError';
import './styles.scss';

const PAGE_SIZE = 6;
const CATEGORY_CONTRACTS = 'contracts';
const CATEGORY_SECTIONS = 'sections';
const SILENT_LOAD = { silent: true };

const categoryTitle = (known, count, singular, plural) => {
  if (!known || count !== 1) {
    if (known && count > 1) return `${count} ${plural}`;
    return plural;
  }
  return `1 ${singular}`;
};

const Payments = () => {
  const literals = useLiterals('payments');
  const contracts = useSelector((state) => state.contracts);
  const sections = useSelector((state) => state.sections);
  const dispatch = useDispatch();
  const location = useLocation();
  const isPaymentsView = useMemo(
    () => !!matchPath({ path: ROUTE_PATH.PAYMENTS }, location.pathname),
    [location],
  );

  const [data, setData] = useState({ contracts: [], sections: [] });
  const [search, setSearch] = useState({ temp: '', search: '' });
  const [resetPage, setResetPage] = useState(0);
  const [expanded, setExpanded] = useState({
    contracts: false,
    sections: false,
  });
  const [pending, setPending] = useState({
    contracts: false,
    sections: false,
  });
  const contractsRef = useRef(contracts);
  const sectionsRef = useRef(sections);
  const requestsInFlight = useRef({
    contracts: false,
    sections: false,
  });
  contractsRef.current = contracts;
  sectionsRef.current = sections;

  const multiplePayment = useMemo(() => {
    return contracts?.list?.length > 1 && contracts.list.filter((contract) => {
      return contract.CapitalRestante > 0 && !contract.seRealizoPago && contract.OpcionesPago.length > 0;
    }).length > 1;
  }, [contracts]);

  const loadCategory = useCallback((category, { force = false } = {}) => {
    const domain = category === CATEGORY_CONTRACTS
      ? contractsRef.current
      : sectionsRef.current;
    const needsFetch = force || !domain.loaded || domain.needUpdate;
    if (!needsFetch || requestsInFlight.current[category]) return;

    requestsInFlight.current[category] = true;
    setPending((current) => ({ ...current, [category]: true }));
    const request = category === CATEGORY_CONTRACTS
      ? getContracts(null, SILENT_LOAD)
      : getSections(null, SILENT_LOAD);
    Promise.resolve(dispatch(request)).finally(() => {
      requestsInFlight.current[category] = false;
      setPending((current) => ({ ...current, [category]: false }));
    });
  }, [dispatch]);

  const toggleSection = (category) => {
    setExpanded((current) => ({
      ...current,
      [category]: !current[category],
    }));
  };

  useEffect(() => {
    if (isPaymentsView) return;
    const params = { has_payments: true };
    dispatch(getContracts(params)).then((response) => {
      setData((prev) => ({ ...prev, contracts: response || [] }));
    });
    dispatch(getSections(params)).then((response) => {
      setData((prev) => ({ ...prev, sections: response || [] }));
    });
  }, [dispatch, isPaymentsView]);

  useEffect(() => {
    if (!isPaymentsView) return;
    if (expanded.contracts) loadCategory(CATEGORY_CONTRACTS);
    if (expanded.sections) loadCategory(CATEGORY_SECTIONS);
  }, [
    isPaymentsView,
    expanded.contracts,
    expanded.sections,
    contracts.loaded,
    contracts.needUpdate,
    sections.loaded,
    sections.needUpdate,
    loadCategory,
  ]);

  const filteredContracts = useMemo(() => {
    const list = isPaymentsView ? contracts?.list || [] : data.contracts;
    return search.search ? list.filter((contract) => {
      return contract.Numero.toLowerCase().includes(search.search);
    }) : list;
  }, [isPaymentsView, contracts?.list, data.contracts, search.search]);

  const filteredSections = useMemo(() => {
    const list = isPaymentsView ? sections?.list || [] : data.sections;
    return search.search ? list.filter((section) => {
      return section.id.toString().includes(search.search);
    }) : list;
  }, [isPaymentsView, sections?.list, data.sections, search.search]);

  const handleSearch = () => {
    const nextSearch = search.temp;
    setSearch({ ...search, search: nextSearch });
    setResetPage(resetPage + 1);
    if (isPaymentsView && nextSearch) {
      setExpanded({ contracts: true, sections: true });
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const contractsKnown = isPaymentsView && contracts.loaded;
  const sectionsKnown = isPaymentsView && sections.loaded;
  const contractHeading = isPaymentsView
    ? categoryTitle(
      contractsKnown,
      filteredContracts.length,
      literals.contract,
      literals.contracts,
    )
    : `${filteredContracts.length ? `${filteredContracts.length} ` : ''}${literals.contracts}`;
  const sectionHeading = isPaymentsView
    ? categoryTitle(
      sectionsKnown,
      filteredSections.length,
      literals.common?.section,
      literals.sections,
    )
    : `${filteredSections.length ? `${filteredSections.length} ` : ''}${literals.sections}`;

  const renderContractCards = () => {
    const multiplePaymentAction = multiplePayment && isPaymentsView ? (
      <div className='payment-accordion__extra'>
        <Button
          size='sm'
          to={ROUTE_PATH.PAYMENTS_MULTIPLE}
          text={literals.multiplePayment}
        />
      </div>
    ) : null;

    if (!filteredContracts.length) {
      return (
        <>
          {multiplePaymentAction}
          <Box padding='sm' className='text-center'>
            {contracts?.error ? (
              <>
                <InlineError error={literals.errorLoadData} />
                {isPaymentsView && (
                  <Button
                    className='mt-4'
                    type='outline-primary'
                    size='sm'
                    text={literals.common?.retry}
                    onClick={() => loadCategory(CATEGORY_CONTRACTS, { force: true })}
                  />
                )}
              </>
            ) : (
              <p className='m-2'>
                {search.search ? literals.noContractsFound : literals.noContracts}
              </p>
            )}
          </Box>
        </>
      );
    }

    return (
      <>
        {multiplePaymentAction}
        <SimpleCardListPaginated
          key={`contracts-${isPaymentsView ? 'payments' : 'reprinting'}`}
          pageSize={PAGE_SIZE}
          resetPage={resetPage}
          cards={filteredContracts.map((contract) => ({
            id: contract.Numero,
            title: contract.Numero,
            image: renderIconCategories(contract.Ramo, contract.isAuto, true),
            description: `${contract.DesEstatus} | ${formatDate(contract?.DesEstatus?.toLowerCase() === 'periodo adicional' ? contract?.FechaPad : contract?.FechaPago, 'DD/MM/YYYY')} | ${contract.NombreSucursal}`,
            link: ROUTE_PATH.setContract(contract.Numero) + (isPaymentsView ? '' : '?fromReprinting=true'),
          }))}
        />
      </>
    );
  };

  const renderSectionCards = () => {
    if (!filteredSections.length) {
      return (
        <Box padding='sm' className='text-center'>
          {sections?.error ? (
            <>
              <InlineError error={literals.errorLoadData} />
              {isPaymentsView && (
                <Button
                  className='mt-4'
                  type='outline-primary'
                  size='sm'
                  text={literals.common?.retry}
                  onClick={() => loadCategory(CATEGORY_SECTIONS, { force: true })}
                />
              )}
            </>
          ) : (
            <p className='m-2'>
              {search.search ? literals.noSectionsFound : literals.noSections}
            </p>
          )}
        </Box>
      );
    }

    return (
      <SimpleCardListPaginated
        key={`sections-${isPaymentsView ? 'payments' : 'reprinting'}`}
        pageSize={PAGE_SIZE}
        resetPage={resetPage}
        cards={filteredSections.map((section) => ({
          id: section.idApartado,
          image: documentIcon,
          title: `${section.id} ${section.description.length > 1 ? translate(literals.sectionProducts, { number: section.description.length }) : ''}`,
          subtitle: section.description.reduce((acc, material) => `${acc} ${material.description}`, ''),
          description: `${section.expirationDate} | ${section.branch}`,
          link: ROUTE_PATH.setSection(section.id) + (isPaymentsView ? '' : '?fromReprinting=true'),
        }))}
      />
    );
  };

  const renderStatus = () => (
    <div className='payment-accordion__status' role='status'>
      <span className='payment-accordion__spinner' aria-hidden='true' />
      <span>{literals.common?.loading}</span>
    </div>
  );

  const renderAccordion = (category, title, busy, body) => {
    const isOpen = expanded[category];
    const panelId = `payment-${category}-panel`;
    return (
      <section className={`payment-accordion${isOpen ? ' is-open' : ''}`}>
        <h3 className='payment-accordion__heading'>
          <button
            type='button'
            className='payment-accordion__header font-sans'
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => toggleSection(category)}
          >
            <span className='payment-accordion__title font-display font-bold text-lg text-neutral-800'>
              {title}
            </span>
            <span className='payment-accordion__action font-sans text-sm font-semibold text-brand leading-none'>
              {isOpen ? literals.hideSection : literals.common?.viewAll}
              <svg
                className={`payment-accordion__chevron${isOpen ? ' is-open' : ''}`}
                viewBox='0 0 24 24'
                width='18'
                height='18'
                aria-hidden='true'
              >
                <path
                  d='M6 9l6 6 6-6'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </span>
          </button>
        </h3>
        <div
          id={panelId}
          className='payment-accordion__panel'
          aria-hidden={!isOpen}
        >
          <div className='payment-accordion__panel-inner'>
            {busy ? renderStatus() : body}
          </div>
        </div>
      </section>
    );
  };

  const contractsBusy = isPaymentsView
    && expanded.contracts
    && !filteredContracts.length
    && (pending.contracts || (!contracts.loaded && !contracts.error));
  const sectionsBusy = isPaymentsView
    && expanded.sections
    && !filteredSections.length
    && (pending.sections || (!sections.loaded && !sections.error));

  return (
    <Layout
      title={isPaymentsView ? literals.title : literals.reprintingReceipts}
      bottomMenu
      onBack
      className='payments-page'
    >
      <AppShell variant='wide'>
        <div className='documents'>
          <div className='searcher mb-6'>
            <Input
              placeholder={literals.contractOrSection}
              aria-label={literals.contractOrSection}
              value={search.temp}
              onChange={(value) => setSearch({ ...search, temp: value })}
              onKeyDown={handleKeyDown}
            />
            <Button
              className='no-hover-btn'
              isIconButton
              onClick={handleSearch}
              aria-label={literals.common?.search}
            >
              <SearchIcon />
            </Button>
          </div>
          {isPaymentsView ? (
            <div className='payment-accordions'>
              {renderAccordion(
                CATEGORY_CONTRACTS,
                contractHeading,
                contractsBusy,
                renderContractCards(),
              )}
              {renderAccordion(
                CATEGORY_SECTIONS,
                sectionHeading,
                sectionsBusy,
                renderSectionCards(),
              )}
            </div>
          ) : (
            <>
              <div className='flex justify-between items-center mb-4 carousel-title'>
                <h3 className='m-0'>{contractHeading}</h3>
              </div>
              {renderContractCards()}
              <h3 className='my-4'>{sectionHeading}</h3>
              {renderSectionCards()}
            </>
          )}
        </div>
      </AppShell>
    </Layout>
  );
};

export default Payments;
